import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import favoritesService from '../services/favoritesService';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const { FiSearch, FiTag, FiCalendar, FiChevronDown, FiChevronUp, FiTrash2, FiFileText, FiDownload, FiInfo, FiX, FiCopy } = FiIcons;

const FavoriteMealItem = ({ favorite, onSelect, isSelected, onDelete, onLoad }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { meal_plan: mealPlan } = favorite;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 flex justify-between items-center cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center gap-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(favorite.id)}
            onClick={(e) => e.stopPropagation()}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-5 h-5"
          />
          <div>
            <div className="font-medium text-gray-800">{`Meal - ${new Date(favorite.favorited_at).toLocaleDateString()}`}</div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
              <SafeIcon icon={FiCalendar} />
              <span>{new Date(favorite.favorited_at).toLocaleString()}</span>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {favorite.tags.map(tag => (
                <span key={tag} className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-xs font-medium">{tag}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onLoad(favorite);
            }}
            className="text-indigo-600 hover:text-indigo-800 p-2 rounded-full hover:bg-indigo-50"
            title="Load to Calculator"
          >
            <SafeIcon icon={FiCopy} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(favorite.id);
            }}
            className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50"
          >
            <SafeIcon icon={FiTrash2} />
          </button>
          <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="text-gray-500" />
        </div>
      </div>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-t border-gray-200 bg-white">
              <h4 className="font-semibold text-gray-700 mb-2">Foods:</h4>
              <ul className="space-y-2 mb-4">
                {mealPlan.foods.map(food => (
                  <li key={food.id} className="text-sm text-gray-600">{food.name} - {food.actualServingAmount} {food.selectedServingUnit}</li>
                ))}
              </ul>
              <h4 className="font-semibold text-gray-700 mb-2">Macros:</h4>
              <div className="grid grid-cols-4 gap-2 text-center text-sm">
                <div className="bg-orange-50 p-2 rounded"><div className="font-bold text-orange-700">{Math.round(mealPlan.totals.calories)}</div><div className="text-xs text-orange-600">Calories</div></div>
                <div className="bg-red-50 p-2 rounded"><div className="font-bold text-red-700">{Math.round(mealPlan.totals.protein)}g</div><div className="text-xs text-red-600">Protein</div></div>
                <div className="bg-yellow-50 p-2 rounded"><div className="font-bold text-yellow-700">{Math.round(mealPlan.totals.carbs)}g</div><div className="text-xs text-yellow-600">Carbs</div></div>
                <div className="bg-green-50 p-2 rounded"><div className="font-bold text-green-700">{Math.round(mealPlan.totals.fat)}g</div><div className="text-xs text-green-600">Fat</div></div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FavoriteMealsList = ({ onLoadFavorite }) => {
  const [favorites, setFavorites] = useState([]);
  const [filteredFavorites, setFilteredFavorites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadFavorites = async () => {
      setIsLoading(true);
      try {
        const favs = await favoritesService.getFavorites();
        setFavorites(favs);
        const tags = new Set(favs.flatMap(f => f.tags));
        setAllTags(['All', ...Array.from(tags)]);
      } catch (error) {
        console.error("Failed to load favorites:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadFavorites();
  }, []);

  useEffect(() => {
    let result = favorites;
    if (selectedTag && selectedTag !== 'All') {
      result = result.filter(fav => fav.tags.includes(selectedTag));
    }
    if (searchTerm) {
      result = result.filter(fav =>
        fav.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    setFilteredFavorites(result);
  }, [searchTerm, selectedTag, favorites]);

  const handleSelect = (id) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
  };

  const handleDelete = async (id) => {
    await favoritesService.removeFavorite(id);
    const updatedFavorites = favorites.filter(fav => fav.id !== id);
    setFavorites(updatedFavorites);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredFavorites.map(f => f.id));
    } else {
      setSelectedIds([]);
    }
  };

  const exportSelectedToPDF = async () => {
    const mealsToExport = favorites.filter(fav => selectedIds.includes(fav.id));
    if (mealsToExport.length === 0) return;

    setExportingPDF(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);
      let yPosition = margin;

      const addFooter = (pageNumber) => {
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(`Page ${pageNumber}`, margin, pdfHeight - 5);
        pdf.text('www.createmymeal.com', pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      };

      const headerContainer = document.createElement('div');
      headerContainer.style.position = 'absolute';
      headerContainer.style.left = '-9999px';
      headerContainer.style.width = `${contentWidth}mm`;
      headerContainer.style.boxSizing = 'border-box';
      headerContainer.innerHTML = `
        <div style="font-family: Arial, sans-serif; color: #333; text-align: center; border-bottom: 2px solid #4f46e5; padding-bottom: 10px;">
          <h1 style="color: #4f46e5; font-size: 28px; margin: 0;">Favorite Meal Plans</h1>
          <p style="color: #666; font-size: 14px; margin-top: 5px;">Generated by CreateMyMeal</p>
        </div>
      `;
      document.body.appendChild(headerContainer);
      const headerCanvas = await html2canvas(headerContainer, { scale: 3 });
      const headerHeight = (headerCanvas.height * contentWidth) / headerCanvas.width;
      pdf.addImage(headerCanvas.toDataURL('image/png'), 'PNG', margin, yPosition, contentWidth, headerHeight);
      yPosition += headerHeight + 5;
      document.body.removeChild(headerContainer);
      addFooter(pdf.internal.getNumberOfPages());

      for (const favorite of mealsToExport) {
        const { meal_plan: mealPlan } = favorite;
        const mealContainer = document.createElement('div');
        mealContainer.style.position = 'absolute';
        mealContainer.style.left = '-9999px';
        mealContainer.style.width = `${contentWidth}mm`;
        mealContainer.style.boxSizing = 'border-box';
        mealContainer.style.backgroundColor = 'white';
        mealContainer.innerHTML = `
          <div style="font-family: Arial, sans-serif; color: #333;">
            <div style="background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px;">
              <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px; margin-bottom: 15px;">
                <h2 style="font-size: 20px; color: #1f2937; margin: 0;">Meal from ${new Date(favorite.favorited_at).toLocaleDateString()}</h2>
                <span style="font-size: 12px; color: #6b7280;">${new Date(favorite.favorited_at).toLocaleTimeString()}</span>
              </div>
              <div style="margin-bottom: 15px;">
                <b>Tags:</b> ${favorite.tags.length > 0 ? favorite.tags.map(tag => `<span style="display: inline-block; background-color: #e0e7ff; color: #4f46e5; padding: 3px 8px; border-radius: 99px; font-size: 12px; margin-right: 5px; white-space: nowrap;">${tag}</span>`).join('') : 'None'}
              </div>
              <div style="display: flex; gap: 20px;">
                <div style="width: 40%;">
                  <h3 style="font-size: 16px; color: #1f2937; margin-bottom: 10px;">Macro Summary</h3>
                  <div style="font-size: 14px; color: #4b5563;">
                    <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #f3f4f6;"><span>Calories:</span> <b>${Math.round(mealPlan.totals.calories)} kcal</b></div>
                    <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #f3f4f6;"><span>Protein:</span> <b>${Math.round(mealPlan.totals.protein)} g</b></div>
                    <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #f3f4f6;"><span>Carbs:</span> <b>${Math.round(mealPlan.totals.carbs)} g</b></div>
                    <div style="display: flex; justify-content: space-between; padding: 5px 0;"><span>Fat:</span> <b>${Math.round(mealPlan.totals.fat)} g</b></div>
                  </div>
                </div>
                <div style="width: 60%; border-left: 1px solid #e5e7eb; padding-left: 20px;">
                  <h3 style="font-size: 16px; color: #1f2937; margin-bottom: 10px;">Food Breakdown</h3>
                  <ul style="list-style-type: none; padding: 0; margin: 0; font-size: 14px;">
                    ${mealPlan.foods.map(f => `<li style="padding: 5px 0; border-bottom: 1px solid #f3f4f6;">${f.name}: <b>${f.actualServingAmount} ${f.selectedServingUnit}</b></li>`).join('')}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        `;
        document.body.appendChild(mealContainer);

        const canvas = await html2canvas(mealContainer, { scale: 3 });
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        if (yPosition + imgHeight > pdfHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          addFooter(pdf.internal.getNumberOfPages());
        }

        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, yPosition, contentWidth, imgHeight);
        yPosition += imgHeight + 5;
        document.body.removeChild(mealContainer);
      }
      pdf.save('Favorite_Meals.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-bold text-gray-800 mb-6">Favorite Meals</h3>
      <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-4">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search by tag..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag === 'All' ? null : tag)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${(selectedTag === tag || (tag === 'All' && !selectedTag)) ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'}`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            onChange={handleSelectAll}
            checked={filteredFavorites.length > 0 && selectedIds.length === filteredFavorites.length}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-5 h-5"
          />
          <label className="text-sm text-gray-600">Select All</label>
        </div>
        <button
          onClick={exportSelectedToPDF}
          disabled={selectedIds.length === 0 || exportingPDF}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-indigo-300 flex items-center gap-2"
        >
          {exportingPDF ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <SafeIcon icon={FiDownload} />
              <span>Export ({selectedIds.length})</span>
            </>
          )}
        </button>
      </div>
      <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
            <p>Loading favorites...</p>
          </div>
        ) : filteredFavorites.length > 0 ? (
          filteredFavorites.map(fav => (
            <FavoriteMealItem
              key={fav.id}
              favorite={fav}
              onSelect={handleSelect}
              isSelected={selectedIds.includes(fav.id)}
              onDelete={handleDelete}
              onLoad={onLoadFavorite}
            />
          ))
        ) : (
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiInfo} className="text-4xl mx-auto mb-2 opacity-50" />
            <p>No favorite meals found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoriteMealsList;